import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { KeycloakService, ToastService } from '@atsystems/ui';
import { Roles } from '../../../common/helpers/roles';
import { feedbackToast } from '../../../common/helpers/feedback-toast';
import { FeedbackKeyboard } from './services/feedback-keyboard';

export interface WidgetLauncher {
  activate(): void;
}
export const FEEDBACK_WIDGET_LOADER = new InjectionToken<() => Promise<WidgetLauncher>>('feedback loader', {
  providedIn: 'root',
  factory: () => {
    const injector = inject(Injector);
    return () => import('./services/feedback-widget-runtime').then(module => injector.get(module.FeedbackWidgetRuntime));
  },
});

@Injectable({ providedIn: 'root' })
export class FeedbackWidgetService {
  private readonly doc = inject(DOCUMENT);
  private readonly auth = inject(KeycloakService);
  private readonly toast = inject(ToastService);
  private readonly load = inject(FEEDBACK_WIDGET_LOADER);
  private loading?: Promise<WidgetLauncher>;
  private navigation = 0;
  private destroyed = false;

  constructor() {
    const win = this.doc.defaultView ?? window;
    // Window capture at app start: ⌘B/Ctrl+B is the feature's own key everywhere, including the open widget
    // (there it is swallowed: the widget is already open), ahead of every page listener.
    win.addEventListener('keydown', this.hotkey, true);
    // The keyboard of the open widget: created now, so it also precedes page listeners added on window later.
    inject(FeedbackKeyboard);
    inject(Router)
      .events.pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (event instanceof NavigationStart) ++this.navigation;
      });
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      win.removeEventListener('keydown', this.hotkey, true);
    });
  }

  async activate(): Promise<void> {
    if (!this.auth.hasResourceRole(Roles.FRONT_ACCESS)) return;
    const navigation = this.navigation;
    try {
      const launcher = await (this.loading ??= this.load());
      if (!this.destroyed && navigation === this.navigation && this.auth.hasResourceRole(Roles.FRONT_ACCESS)) launcher.activate();
    } catch {
      this.loading = undefined;
      if (!this.destroyed) this.toast.add(feedbackToast('error', 'Could not load feedback. Please try again.'));
    }
  }

  private hotkey = (event: KeyboardEvent): void => {
    if (
      !(event.metaKey || event.ctrlKey) ||
      event.altKey ||
      event.shiftKey ||
      event.code !== 'KeyB' ||
      !this.auth.hasResourceRole(Roles.FRONT_ACCESS)
    )
      return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!event.repeat) void this.activate();
  };
}
